// src/components/dashboard/UploadModal.tsx
import React, { useState, useRef, useEffect } from 'react';
import { X, Upload, FileText, CheckCircle, AlertCircle, Loader, Sparkles } from 'lucide-react';
import { apiService } from '@/services/api';
import { useToast } from '@/components/ui/toast';
import { ProcessingInfo } from '@/components/health/ProcessingInfo';
import { PrescriptionProcessingInfo } from '@/components/health/PrescriptionProcessingInfo';
import { PasswordModal } from '@/components/ui/PasswordModal';

interface UploadModalProps {
  isOpen: boolean;
  onClose: () => void;
  onUploadComplete?: () => void;
  onShowPreview?: (data: any, jobId: string) => void;
  onNavigateToPrescription?: () => void;
}

interface JobResult {
  filename: string;
  file_type: string;
  patient_info: any;
  health_metrics: any[];
  processing_info: any;
  metrics_count: number;
  report_id?: string;
}

interface FileStatus {
  file: File;
  status: 'pending' | 'uploading' | 'processing' | 'completed' | 'error';
  progress: number;
  stage: string;
  error?: string;
  jobId?: string;
}

export const UploadModal: React.FC<UploadModalProps> = ({
  isOpen,
  onClose,
  onUploadComplete,
  onShowPreview,
  onNavigateToPrescription
}) => {
  const [documentType, setDocumentType] = useState<'lab_report' | 'prescription' | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [fileStatuses, setFileStatuses] = useState<FileStatus[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [currentProcessingFile, setCurrentProcessingFile] = useState<{
    fileName: string;
    progress: number;
    stage: string;
    status: 'processing' | 'completed' | 'failed';
    errorCode?: string;
    errorType?: string;
    errorMessage?: string;
  } | null>(null);

  // State for prescription processing
  const [prescriptionProcessing, setPrescriptionProcessing] = useState<{
    fileName: string;
    status: 'processing' | 'completed' | 'failed';
    errorCode?: string;
    errorType?: string;
    errorMessage?: string;
  } | null>(null);

  // State for password-protected PDF handling
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [passwordProtectedFile, setPasswordProtectedFile] = useState<File | null>(null);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const toast = useToast();

  // Mobile viewport detection
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 480);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Helper function to detect password-protected PDF errors
  const isPasswordProtectedError = (errorMessage: string): boolean => {
    const lowerMessage = errorMessage.toLowerCase();
    return (
      lowerMessage.includes('password-protected') ||
      lowerMessage.includes('password protected') ||
      lowerMessage.includes('provide the password') ||
      lowerMessage.includes('pdf is encrypted') ||
      lowerMessage.includes('password required') ||
      lowerMessage.includes('pdf_password_required') ||
      lowerMessage.includes('invalid_password')
    );
  };

  if (!isOpen) return null;

  // File validation function
  const validateFile = (file: File): { valid: boolean; error?: string } => {
    const MAX_FILE_SIZE = parseInt(import.meta.env.VITE_MAX_FILE_SIZE || '52428800'); // 50MB default
    const ALLOWED_TYPES = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/heic'];

    // Check file type
    if (!ALLOWED_TYPES.includes(file.type.toLowerCase())) {
      return {
        valid: false,
        error: `Invalid file type. Please upload PDF, JPG, JPEG, PNG, WEBP, or HEIC files only.`
      };
    }

    // Check file size
    if (file.size > MAX_FILE_SIZE) {
      const sizeMB = (MAX_FILE_SIZE / (1024 * 1024)).toFixed(0);
      return {
        valid: false,
        error: `File too large. Maximum size is ${sizeMB}MB. Your file is ${(file.size / (1024 * 1024)).toFixed(1)}MB.`
      };
    }

    return { valid: true };
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);

    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      const filesArray = Array.from(e.dataTransfer.files);

      // Validate only the first file for single file upload
      const file = filesArray[0];
      const validation = validateFile(file);

      if (validation.valid) {
        setSelectedFiles([file]);
      } else {
        toast.error(`${file.name}: ${validation.error}`);
      }
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const filesArray = Array.from(e.target.files);

      // Validate only the first file for single file upload
      const file = filesArray[0];
      const validation = validateFile(file);

      if (validation.valid) {
        setSelectedFiles([file]);
      } else {
        toast.error(`${file.name}: ${validation.error}`);
      }
    }
  };

  const handleBrowseClick = () => {
    fileInputRef.current?.click();
  };

  const handleUpload = async () => {
    if (selectedFiles.length === 0 || !documentType) return;

    setIsProcessing(true);
    const file = selectedFiles[0];

    // Initialize file status
    const initialStatus: FileStatus = {
      file,
      status: 'pending',
      progress: 0,
      stage: 'Waiting to upload...'
    };
    setFileStatuses([initialStatus]);

    try {
      // Handle prescription vs lab report differently
      if (documentType === 'prescription') {
        // Show prescription processing screen
        setPrescriptionProcessing({
          fileName: file.name,
          status: 'processing'
        });

        // Upload prescription - synchronous, returns success immediately
        const response = await apiService.uploadPrescription(file);

        if (!response.success) {
          throw new Error(response.error?.message || 'Prescription upload failed');
        }

        // Update to completed state
        setPrescriptionProcessing({
          fileName: file.name,
          status: 'completed'
        });

        setFileStatuses([{ ...initialStatus, status: 'completed', progress: 100, stage: 'Upload complete!' }]);


        // Wait for processing screen animation to complete
        await new Promise(resolve => setTimeout(resolve, 1500));

        // Navigate to prescription tab
        if (onNavigateToPrescription) {
          setPrescriptionProcessing(null);
          setIsProcessing(false);
          setSelectedFiles([]);
          setFileStatuses([]);
          onClose();
          onNavigateToPrescription();
          return;
        }

        if (onUploadComplete) {
          onUploadComplete();
        }
      } else {
        // Lab report processing - show real-time progress
        setCurrentProcessingFile({
          fileName: file.name,
          progress: 0,
          stage: 'Uploading document...',
          status: 'processing'
        });

        // Update status to uploading
        setFileStatuses([{
          file,
          status: 'uploading',
          progress: 0,
          stage: 'Uploading document...'
        }]);

        // Step 1: Start async background job
        const asyncJobResponse = await apiService.extractHealthMetricsAsync(file);
        const jobId = asyncJobResponse.data.job_id;

        // Step 2: Poll job until complete with real-time progress updates
        const jobStatusResponse = await apiService.pollJobUntilComplete(
          jobId,
          (progress, stage) => {
            // Update UI with real-time progress
            setCurrentProcessingFile({
              fileName: file.name,
              progress,
              stage,
              status: 'processing'
            });

            setFileStatuses([{ file, status: 'processing', progress, stage, jobId }]);
          }
        );

        // Extract result data from completed job
        const jobResultData: JobResult | null = jobStatusResponse.data.result || jobStatusResponse.data.result_data || null;

        if (!jobResultData) {
          console.error('❌ [UploadModal] Job completed but no result data returned from backend!');
          throw new Error('Processing completed but no result data received');
        }

        console.log('✅ [UploadModal] Job completed successfully with result data:', {
          filename: jobResultData.filename,
          metricsCount: jobResultData.metrics_count,
          reportId: jobResultData.report_id
        });

        // Step 3: Update UI to show completion
        setCurrentProcessingFile({
          fileName: file.name,
          progress: 100,
          stage: 'Analysis complete!',
          status: 'completed'
        });

        setFileStatuses([{ file, status: 'completed', progress: 100, stage: 'Analysis complete!', jobId }]);


        // Wait a moment to show completion state
        await new Promise(resolve => setTimeout(resolve, 1500));

        // Step 4: Navigate to ExtractedResultsView to show the results
        if (onShowPreview) {
          console.log('✅ [UploadModal] Navigating to ExtractedResultsView...');

          // Clean up modal state
          setCurrentProcessingFile(null);
          setIsProcessing(false);
          setSelectedFiles([]);
          setFileStatuses([]);

          // Close modal
          onClose();

          // Navigate to preview page
          onShowPreview(jobResultData, jobId);
          return;
        }

        // Fallback: No preview callback, just refresh dashboard
        if (onUploadComplete) {
          onUploadComplete();
        }
      }
    } catch (error: any) {
      console.error(`Error processing ${file.name}:`, error);

      const errorCode = error.code || '';
      const errorType = error.type || '';
      const errorMessage = error.message || 'Processing failed';

      const isTimeoutError = errorMessage.includes('polling timeout') || errorMessage.includes('took too long');
      const isQuotaError = (
        errorCode === 'QUOTA_EXCEEDED' ||
        errorType === 'quota_exceeded' ||
        errorType === 'LLM_WHISPERER_LIMIT' ||
        errorMessage.toLowerCase().includes('quota') ||
        errorMessage.toLowerCase().includes('limit reached')
      );

      if (documentType === 'prescription') {
        setPrescriptionProcessing({
          fileName: file.name,
          status: 'failed',
          errorCode: isQuotaError ? 'QUOTA_EXCEEDED' : errorCode,
          errorType: isQuotaError ? 'quota_exceeded' : errorType,
          errorMessage: isQuotaError ? "Hey sorry, looks like a lot of people processed a lot of PDFs today and we've hit our quota for today. We're sorry, we're still a small operation and would request you to come back tomorrow!" : errorMessage
        });
      } else {
        if (isTimeoutError) {
          setCurrentProcessingFile({
            fileName: file.name,
            progress: 95,
            stage: 'Processing may have completed - check dashboard',
            status: 'failed',
            errorCode,
            errorType,
            errorMessage
          });

          setFileStatuses([{
            file,
            status: 'error',
            progress: 95,
            stage: 'Check dashboard',
            error: 'Processing timeout - data may be saved. Refresh dashboard to check.'
          }]);


        } else if (isQuotaError) {
          setCurrentProcessingFile({
            fileName: file.name,
            progress: 0,
            stage: 'Daily limit reached',
            status: 'failed',
            errorCode: 'QUOTA_EXCEEDED',
            errorType: 'quota_exceeded',
            errorMessage: "Hey sorry, looks like a lot of people processed a lot of PDFs today and we've hit our quota for today. We're sorry, we're still a small operation and would request you to come back tomorrow!"
          });

          setFileStatuses([{
            file,
            status: 'error',
            progress: 0,
            stage: 'Quota exceeded',
            error: errorMessage
          }]);
        } else if (isPasswordProtectedError(errorMessage)) {
          // Password-protected PDF detected - show password modal
          console.log('🔐 [UploadModal] Password-protected PDF detected, showing password modal');
          setCurrentProcessingFile(null);
          setIsProcessing(false);
          setPasswordProtectedFile(file);
          setShowPasswordModal(true);
        } else {
          setCurrentProcessingFile({
            fileName: file.name,
            progress: 0,
            stage: errorMessage,
            status: 'failed',
            errorCode,
            errorType,
            errorMessage
          });

          setFileStatuses([{
            file,
            status: 'error',
            progress: 0,
            stage: 'Failed',
            error: errorMessage
          }]);

          toast.error(`Failed to process ${file.name}: ${errorMessage}`);
        }
      }
    } finally {
      // Final cleanup only if not showing preview
      const hasErrors = fileStatuses.some(fs => fs.status === 'error');
      if (!hasErrors) {
        setIsProcessing(false);
      }
    }
  };

  const handleRemoveFile = (index: number) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  // Handler for password submission from PasswordModal
  const handlePasswordSubmit = async (password: string): Promise<void> => {
    if (!passwordProtectedFile || !documentType) {
      throw new Error('No file to process');
    }

    const file = passwordProtectedFile;

    // Close the password modal
    setShowPasswordModal(false);
    setPasswordProtectedFile(null);

    // Re-start processing with the password
    setIsProcessing(true);

    try {
      if (documentType === 'prescription') {
        // Show prescription processing screen
        setPrescriptionProcessing({
          fileName: file.name,
          status: 'processing'
        });

        // Upload prescription with password
        const response = await apiService.uploadPrescription(file, password);

        if (!response.success) {
          throw new Error(response.error?.message || 'Prescription upload failed');
        }

        // Update to completed state
        setPrescriptionProcessing({
          fileName: file.name,
          status: 'completed'
        });

        // Wait for processing screen animation to complete
        await new Promise(resolve => setTimeout(resolve, 1500));

        // Navigate to prescription tab
        if (onNavigateToPrescription) {
          setPrescriptionProcessing(null);
          setIsProcessing(false);
          setSelectedFiles([]);
          setFileStatuses([]);
          onClose();
          onNavigateToPrescription();
          return;
        }

        if (onUploadComplete) {
          onUploadComplete();
        }
      } else {
        // Lab report processing with password
        setCurrentProcessingFile({
          fileName: file.name,
          progress: 0,
          stage: 'Uploading document with password...',
          status: 'processing'
        });

        // Step 1: Start async background job with password
        const asyncJobResponse = await apiService.extractHealthMetricsAsync(file, password);
        const jobId = asyncJobResponse.data.job_id;

        // Step 2: Poll job until complete
        const jobStatusResponse = await apiService.pollJobUntilComplete(
          jobId,
          (progress, stage) => {
            setCurrentProcessingFile({
              fileName: file.name,
              progress,
              stage,
              status: 'processing'
            });
            setFileStatuses([{ file, status: 'processing', progress, stage, jobId }]);
          }
        );

        // Extract result data
        const jobResultData = jobStatusResponse.data.result || jobStatusResponse.data.result_data || null;

        if (!jobResultData) {
          throw new Error('Processing completed but no result data received');
        }

        console.log('✅ [UploadModal] Job completed successfully with password');

        // Update UI to show completion
        setCurrentProcessingFile({
          fileName: file.name,
          progress: 100,
          stage: 'Analysis complete!',
          status: 'completed'
        });

        setFileStatuses([{ file, status: 'completed', progress: 100, stage: 'Analysis complete!', jobId }]);

        // Wait a moment to show completion state
        await new Promise(resolve => setTimeout(resolve, 1500));

        // Navigate to ExtractedResultsView
        if (onShowPreview) {
          setCurrentProcessingFile(null);
          setIsProcessing(false);
          setSelectedFiles([]);
          setFileStatuses([]);
          onClose();
          onShowPreview(jobResultData, jobId);
          return;
        }

        if (onUploadComplete) {
          onUploadComplete();
        }
      }
    } catch (error: any) {
      console.error('Error processing with password:', error);
      const errorMessage = error.message || 'Processing failed';

      // Check if it's still a password error (wrong password)
      if (isPasswordProtectedError(errorMessage)) {
        // Show password modal again with the same file
        setCurrentProcessingFile(null);
        setPrescriptionProcessing(null);
        setIsProcessing(false);
        setPasswordProtectedFile(file);
        setShowPasswordModal(true);
        throw new Error('Invalid password. Please try again.');
      }

      // Other errors - show in processing info
      if (documentType === 'prescription') {
        setPrescriptionProcessing({
          fileName: file.name,
          status: 'failed',
          errorMessage
        });
      } else {
        setCurrentProcessingFile({
          fileName: file.name,
          progress: 0,
          stage: errorMessage,
          status: 'failed',
          errorMessage
        });
      }
      throw error;
    }
  };

  // Handler for closing password modal
  const handlePasswordModalClose = () => {
    setShowPasswordModal(false);
    setPasswordProtectedFile(null);
    setSelectedFiles([]);
    setFileStatuses([]);
  };

  return (
    <>
      <div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: 'var(--space-4)',
          backdropFilter: 'blur(4px)'
        }}
        onClick={onClose}
      >
        <div
          style={{
            backgroundColor: 'var(--color-background)',
            borderRadius: '8px',
            maxWidth: (currentProcessingFile || prescriptionProcessing) ? '520px' : '600px',
            width: '100%',
            maxHeight: '90vh',
            overflow: 'auto',
            boxShadow: 'var(--shadow-2xl)',
            position: 'relative'
          }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Show PrescriptionProcessingInfo for prescriptions */}
          {prescriptionProcessing ? (
            <PrescriptionProcessingInfo
              fileName={prescriptionProcessing.fileName}
              status={prescriptionProcessing.status}
              errorMessage={prescriptionProcessing.errorMessage}
              errorCode={prescriptionProcessing.errorCode}
              errorType={prescriptionProcessing.errorType}
              variant="embedded"
              onRetry={() => {
                setPrescriptionProcessing(null);
                setIsProcessing(false);
                onClose();
              }}
              onComplete={() => {
                // This is called after the completion animation
                if (onNavigateToPrescription) {
                  setPrescriptionProcessing(null);
                  setIsProcessing(false);
                  setSelectedFiles([]);
                  setFileStatuses([]);
                  onClose();
                  onNavigateToPrescription();
                }
              }}
            />
          ) : currentProcessingFile ? (
            <ProcessingInfo
              fileName={currentProcessingFile.fileName}
              progress={currentProcessingFile.progress}
              stage={currentProcessingFile.stage}
              status={currentProcessingFile.status}
              errorCode={currentProcessingFile.errorCode}
              errorType={currentProcessingFile.errorType}
              errorMessage={currentProcessingFile.errorMessage}
              variant="embedded"
              onRetry={() => {
                // Close the modal and let user try again
                setCurrentProcessingFile(null);
                setIsProcessing(false);
                onClose();
              }}
            />
          ) : (
            <>
              {/* Header */}
              <div style={{
                padding: 'var(--space-6)',
                borderBottom: '1px solid var(--color-border)',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}>
                <div>
                  <h2 style={{
                    fontFamily: 'var(--font-primary)',
                    fontSize: 'var(--text-2xl)',
                    fontWeight: 'var(--font-bold)',
                    color: 'var(--color-text)',
                    margin: 0
                  }}>
                    Upload Health Report
                  </h2>
                  <p style={{
                    fontFamily: 'var(--font-primary)',
                    fontSize: 'var(--text-sm)',
                    color: 'var(--color-muted)',
                    margin: 'var(--space-1) 0 0 0'
                  }}>
                    Upload your lab reports, prescriptions, or medical records
                  </p>
                </div>
                <button
                  onClick={onClose}
                  style={{
                    padding: 'var(--space-2)',
                    backgroundColor: 'transparent',
                    border: 'none',
                    borderRadius: 'var(--radius)',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'var(--color-muted)',
                    transition: 'var(--transition-fast)'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = 'var(--color-hover-bg)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }}
                >
                  <X size={24} />
                </button>
              </div>

              {/* Content */}
              <div style={{ padding: 'var(--space-6)' }}>
                {/* Coming Soon Banner */}
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 'var(--space-3)',
                  padding: 'var(--space-3) var(--space-4)',
                  backgroundColor: 'rgba(59, 130, 246, 0.1)',
                  borderRadius: 'var(--radius)',
                  border: '1px solid rgba(59, 130, 246, 0.2)',
                  marginBottom: 'var(--space-6)',
                  boxShadow: '0 4px 12px rgba(59, 130, 246, 0.05)'
                }}>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: '32px',
                    height: '32px',
                    backgroundColor: 'rgba(59, 130, 246, 0.15)',
                    borderRadius: '50%',
                    flexShrink: 0
                  }}>
                    <Sparkles size={18} color="#3b82f6" />
                  </div>
                  <p style={{
                    fontFamily: 'var(--font-primary)',
                    fontSize: 'var(--text-sm)',
                    lineHeight: '1.5',
                    color: 'var(--color-text)',
                    margin: 0,
                    opacity: 0.9
                  }}>
                    <span style={{ fontWeight: 'var(--font-bold)', color: '#3b82f6' }}>Coming Soon:</span> Multiple document and image uploads. We're working on making this possible for you!
                  </p>
                </div>
                {/* Document Type Selection */}
                {!documentType ? (
                  <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 'var(--space-4)',
                    marginBottom: 'var(--space-6)'
                  }}>
                    <h3 style={{
                      fontFamily: 'var(--font-primary)',
                      fontSize: 'var(--text-base)',
                      fontWeight: 'var(--font-semibold)',
                      color: 'var(--color-text)',
                      margin: '0 0 var(--space-2) 0'
                    }}>
                      Select Document Type
                    </h3>

                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
                      gap: 'var(--space-3)'
                    }}>
                      {/* Prescription Button */}
                      <button
                        onClick={() => setDocumentType('prescription')}
                        style={{
                          padding: 'var(--space-6) var(--space-4)',
                          backgroundColor: 'var(--color-surface)',
                          border: '2px solid var(--color-border)',
                          borderRadius: 'var(--radius)',
                          cursor: 'pointer',
                          fontFamily: 'var(--font-primary)',
                          fontSize: 'var(--text-base)',
                          fontWeight: 'var(--font-medium)',
                          color: 'var(--color-text)',
                          transition: 'var(--transition-fast)',
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          gap: 'var(--space-3)'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = 'var(--color-hover-bg)';
                          e.currentTarget.style.borderColor = 'var(--color-primary)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = 'var(--color-surface)';
                          e.currentTarget.style.borderColor = 'var(--color-border)';
                        }}
                      >
                        <FileText size={32} color="var(--color-primary)" />
                        <span>Prescription</span>
                      </button>

                      {/* Lab Report Button */}
                      <button
                        onClick={() => setDocumentType('lab_report')}
                        style={{
                          padding: 'var(--space-6) var(--space-4)',
                          backgroundColor: 'var(--color-surface)',
                          border: '2px solid var(--color-border)',
                          borderRadius: 'var(--radius)',
                          cursor: 'pointer',
                          fontFamily: 'var(--font-primary)',
                          fontSize: 'var(--text-base)',
                          fontWeight: 'var(--font-medium)',
                          color: 'var(--color-text)',
                          transition: 'var(--transition-fast)',
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          gap: 'var(--space-3)'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = 'var(--color-hover-bg)';
                          e.currentTarget.style.borderColor = 'var(--color-primary)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = 'var(--color-surface)';
                          e.currentTarget.style.borderColor = 'var(--color-border)';
                        }}
                      >
                        <FileText size={32} color="var(--color-primary)" />
                        <span>Lab Report</span>
                      </button>
                    </div>
                  </div>
                ) : (
                  <>
                    {/* Selected Document Type Badge */}
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      padding: 'var(--space-3)',
                      backgroundColor: 'var(--color-accent-blue-bg)',
                      borderRadius: 'var(--radius)',
                      marginBottom: 'var(--space-4)',
                      border: '1px solid var(--color-primary)'
                    }}>
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 'var(--space-2)'
                      }}>
                        <FileText size={20} color="var(--color-primary)" />
                        <span style={{
                          fontFamily: 'var(--font-primary)',
                          fontSize: 'var(--text-sm)',
                          fontWeight: 'var(--font-medium)',
                          color: 'var(--color-text)'
                        }}>
                          {documentType === 'prescription' ? 'Prescription' : 'Lab Report'}
                        </span>
                      </div>
                      {!isProcessing && (
                        <button
                          onClick={() => {
                            setDocumentType(null);
                            setSelectedFiles([]);
                            setFileStatuses([]);
                          }}
                          style={{
                            padding: 'var(--space-1)',
                            backgroundColor: 'transparent',
                            border: 'none',
                            cursor: 'pointer',
                            color: 'var(--color-muted)',
                            display: 'flex',
                            alignItems: 'center'
                          }}
                        >
                          <X size={16} />
                        </button>
                      )}
                    </div>

                    {/* Drop Zone */}
                    <div
                      onDragOver={handleDragOver}
                      onDragLeave={handleDragLeave}
                      onDrop={handleDrop}
                      style={{
                        border: isDragging ? '2px dashed var(--color-primary)' : '2px dashed var(--color-border)',
                        borderRadius: '8px',
                        padding: 'var(--space-12)',
                        textAlign: 'center',
                        backgroundColor: isDragging ? 'var(--color-primary-bg)' : 'var(--color-surface)',
                        transition: 'all 0.2s ease',
                        cursor: 'pointer',
                        marginBottom: 'var(--space-6)'
                      }}
                      onClick={handleBrowseClick}
                    >
                      <input
                        ref={fileInputRef}
                        type="file"
                        onChange={handleFileSelect}
                        accept=".pdf,.jpg,.jpeg,.png,.webp,.heic"
                        style={{ display: 'none' }}
                      />

                      <div style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: 'var(--space-4)'
                      }}>
                        <div style={{
                          width: '64px',
                          height: '64px',
                          borderRadius: '50%',
                          backgroundColor: isDragging ? 'var(--color-accent-blue-bg)' : 'var(--color-hover-bg)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          transition: 'var(--transition-fast)'
                        }}>
                          <Upload size={32} color={isDragging ? 'var(--color-primary)' : 'var(--color-muted)'} />
                        </div>

                        <div>
                          <p style={{
                            fontFamily: 'var(--font-primary)',
                            fontSize: 'var(--text-base)',
                            fontWeight: 'var(--font-semibold)',
                            color: 'var(--color-text)',
                            margin: 0
                          }}>
                            Drop your files here or{' '}
                            <span style={{
                              color: '#3b82f6',
                              cursor: 'pointer',
                              textDecoration: 'underline',
                              fontWeight: '500'
                            }}>browse</span>
                          </p>
                          <p style={{
                            fontFamily: 'var(--font-primary)',
                            fontSize: 'var(--text-sm)',
                            color: 'var(--color-muted)',
                            margin: 'var(--space-2) 0 0 0'
                          }}>
                            Supported formats: PDF, JPG, PNG, WEBP, HEIC (Max 50MB)
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Selected Files */}
                    {selectedFiles.length > 0 && (
                      <div style={{
                        marginBottom: 'var(--space-6)'
                      }}>
                        <h3 style={{
                          fontFamily: 'var(--font-primary)',
                          fontSize: 'var(--text-base)',
                          fontWeight: 'var(--font-semibold)',
                          color: 'var(--color-text)',
                          margin: '0 0 var(--space-3) 0'
                        }}>
                          {isProcessing ? 'Processing Files' : `Selected Files (${selectedFiles.length})`}
                        </h3>

                        <div style={{
                          display: 'flex',
                          flexDirection: 'column',
                          gap: 'var(--space-2)'
                        }}>
                          {selectedFiles.map((file, index) => {
                            const fileStatus = fileStatuses[index];
                            const statusIcon = fileStatus?.status === 'completed' ? (
                              <CheckCircle size={20} color="#10B981" />
                            ) : fileStatus?.status === 'error' ? (
                              <AlertCircle size={20} color="#EF4444" />
                            ) : fileStatus?.status === 'uploading' || fileStatus?.status === 'processing' ? (
                              <Loader size={20} color="#3B82F6" style={{ animation: 'spin 1s linear infinite' }} />
                            ) : (
                              <FileText size={20} color="#3B82F6" />
                            );

                            const statusColor = fileStatus?.status === 'completed' ? '#10B981' :
                              fileStatus?.status === 'error' ? '#EF4444' :
                                fileStatus?.status === 'uploading' || fileStatus?.status === 'processing' ? '#3B82F6' :
                                  '#6B7280';

                            return (
                              <div
                                key={index}
                                style={{
                                  padding: 'var(--space-3)',
                                  backgroundColor: 'var(--color-surface)',
                                  borderRadius: 'var(--radius)',
                                  border: '1px solid var(--color-border)'
                                }}
                              >
                                <div style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'space-between',
                                  marginBottom: fileStatus && fileStatus.status !== 'pending' ? 'var(--space-2)' : 0
                                }}>
                                  <div style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 'var(--space-3)',
                                    flex: 1,
                                    minWidth: 0
                                  }}>
                                    <div style={{
                                      width: '40px',
                                      height: '40px',
                                      borderRadius: 'var(--radius)',
                                      backgroundColor: fileStatus?.status === 'completed' ? 'var(--color-success-bg)' :
                                        fileStatus?.status === 'error' ? 'var(--color-error-bg)' :
                                          'var(--color-accent-blue-bg)',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      flexShrink: 0
                                    }}>
                                      {statusIcon}
                                    </div>
                                    <div style={{ flex: 1, minWidth: 0 }}>
                                      <p style={{
                                        fontFamily: 'var(--font-primary)',
                                        fontSize: 'var(--text-sm)',
                                        fontWeight: 'var(--font-medium)',
                                        color: 'var(--color-text)',
                                        margin: 0,
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap'
                                      }}>
                                        {file.name}
                                      </p>
                                      <p style={{
                                        fontFamily: 'var(--font-primary)',
                                        fontSize: 'var(--text-xs)',
                                        color: statusColor,
                                        margin: 0
                                      }}>
                                        {fileStatus?.stage || formatFileSize(file.size)}
                                      </p>
                                    </div>
                                  </div>
                                  {!isProcessing && (
                                    <button
                                      onClick={() => handleRemoveFile(index)}
                                      style={{
                                        padding: 'var(--space-1)',
                                        backgroundColor: 'transparent',
                                        border: 'none',
                                        borderRadius: 'var(--radius)',
                                        cursor: 'pointer',
                                        color: 'var(--color-error)',
                                        transition: 'var(--transition-fast)'
                                      }}
                                      onMouseEnter={(e) => {
                                        e.currentTarget.style.backgroundColor = 'var(--color-error-bg)';
                                      }}
                                      onMouseLeave={(e) => {
                                        e.currentTarget.style.backgroundColor = 'transparent';
                                      }}
                                    >
                                      <X size={16} />
                                    </button>
                                  )}
                                </div>

                                {/* Progress Bar */}
                                {fileStatus && (fileStatus.status === 'uploading' || fileStatus.status === 'processing') && (
                                  <div style={{
                                    width: '100%',
                                    height: '4px',
                                    backgroundColor: 'var(--color-border)',
                                    borderRadius: '2px',
                                    overflow: 'hidden'
                                  }}>
                                    <div style={{
                                      width: `${fileStatus.progress}%`,
                                      height: '100%',
                                      backgroundColor: 'var(--color-primary)',
                                      transition: 'width 0.3s ease'
                                    }} />
                                  </div>
                                )}

                                {/* Error Message */}
                                {fileStatus?.status === 'error' && fileStatus.error && (
                                  <p style={{
                                    fontFamily: 'var(--font-primary)',
                                    fontSize: 'var(--text-xs)',
                                    color: 'var(--color-error)',
                                    margin: 'var(--space-2) 0 0 0',
                                    fontStyle: 'italic'
                                  }}>
                                    {fileStatus.error}
                                  </p>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>

              {/* Footer */}
              <div style={{
                padding: 'var(--space-6)',
                borderTop: '1px solid var(--color-border)',
                display: 'flex',
                flexDirection: isMobile ? 'column-reverse' : 'row',
                justifyContent: 'flex-end',
                gap: 'var(--space-3)',
                backgroundColor: 'var(--color-surface)'
              }}>
                <button
                  onClick={onClose}
                  disabled={isProcessing}
                  style={{
                    padding: 'var(--space-3) var(--space-6)',
                    backgroundColor: 'var(--color-background)',
                    border: '1px solid var(--color-border)',
                    borderRadius: 'var(--radius)',
                    cursor: isProcessing ? 'not-allowed' : 'pointer',
                    fontFamily: 'var(--font-primary)',
                    fontSize: 'var(--text-sm)',
                    fontWeight: 'var(--font-medium)',
                    color: isProcessing ? 'var(--color-muted)' : 'var(--color-text)',
                    transition: 'var(--transition-fast)',
                    opacity: isProcessing ? 0.5 : 1,
                    width: isMobile ? '100%' : 'auto'
                  }}
                  onMouseEnter={(e) => {
                    if (!isProcessing) {
                      e.currentTarget.style.backgroundColor = 'var(--color-hover-bg)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!isProcessing) {
                      e.currentTarget.style.backgroundColor = 'var(--color-background)';
                    }
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={handleUpload}
                  disabled={selectedFiles.length === 0 || isProcessing}
                  style={{
                    padding: 'var(--space-3) var(--space-6)',
                    backgroundColor: (selectedFiles.length === 0 || isProcessing) ? 'var(--color-border)' : 'var(--color-text)',
                    border: 'none',
                    borderRadius: 'var(--radius)',
                    cursor: (selectedFiles.length === 0 || isProcessing) ? 'not-allowed' : 'pointer',
                    fontFamily: 'var(--font-primary)',
                    fontSize: 'var(--text-sm)',
                    fontWeight: 'var(--font-semibold)',
                    color: (selectedFiles.length === 0 || isProcessing) ? 'var(--color-muted)' : 'var(--color-background)',
                    transition: 'var(--transition-fast)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 'var(--space-2)',
                    width: isMobile ? '100%' : 'auto'
                  }}
                  onMouseEnter={(e) => {
                    if (selectedFiles.length > 0 && !isProcessing) {
                      e.currentTarget.style.opacity = '0.9';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (selectedFiles.length > 0 && !isProcessing) {
                      e.currentTarget.style.opacity = '1';
                    }
                  }}
                >
                  {isProcessing ? (
                    <>
                      <Loader size={16} style={{ animation: 'spin 1s linear infinite' }} />
                      <span>Processing...</span>
                    </>
                  ) : (
                    <>
                      <Upload size={16} />
                      <span>Upload {selectedFiles.length > 0 ? `(${selectedFiles.length})` : ''}</span>
                    </>
                  )}
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Password Modal for password-protected PDFs */}
      <PasswordModal
        isOpen={showPasswordModal}
        fileName={passwordProtectedFile?.name || ''}
        onClose={handlePasswordModalClose}
        onSubmit={handlePasswordSubmit}
      />
    </>
  );
};
